import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import BooksList from './BooksList';
import NotFound from './NotFound';
import { bookList, getBookTitleParts } from './utils';
import { installRecordCardHelpers } from './recordCard';
import { usePageMeta } from './usePageMeta';
import { bookPageDescription } from './seo';
import { BLOBS_BASE_URL } from './constants';
import './styles.scss';

installRecordCardHelpers();

const Book = lazy(() => import('./Book'));
const About = lazy(() => import('./About'));

export function AppHeader({ isBookHeader, showBack, dimLogo, children, onBookBack, biteSelected, bookTitle, bitePosition, biteTotal, backLabel, showMenuButton, menuOpen, onMenuClick, hideNavButton, comingSoon }) {
  const location = useLocation();
  const clickable = (isBookHeader || showBack) && onBookBack && !showMenuButton && !hideNavButton;
  const aboutLinkVisible = !isBookHeader;
  const headerIsButton = clickable && !aboutLinkVisible;
  const logoIsButton = clickable && aboutLinkVisible;
  const brandTogglesMenu = !!showMenuButton && !!onMenuClick;
  const titleParts = getBookTitleParts(bookTitle);

  return (
    <div
      className={`app-header ${isBookHeader ? 'app-header-in-book' : ''}${showBack ? ' app-header-show-back' : ''}${dimLogo ? ' app-header-dim-logo' : ''}${biteSelected ? ' app-header-bite-selected' : ''}${showMenuButton ? ' app-header-has-menu' : ''}${hideNavButton ? ' app-header-nav-hidden' : ''}`}
      onClick={clickable ? onBookBack : undefined}
      onKeyDown={
        headerIsButton
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onBookBack();
              }
            }
          : undefined
      }
      tabIndex={headerIsButton ? 0 : undefined}
      role={headerIsButton ? 'button' : undefined}
      aria-label={headerIsButton ? (backLabel || 'Back') : undefined}
    >
      {showMenuButton ? (
        <button
          type="button"
          className={`books-mobile-menu-btn${menuOpen ? ' books-mobile-menu-btn-open' : ''}`}
          aria-label={menuOpen ? 'Close book list' : 'Open book list'}
          aria-expanded={menuOpen}
          onClick={onMenuClick}
        >
          <img
            src={`${process.env.PUBLIC_URL}/icons/${menuOpen ? 'close' : 'menu'}.svg`}
            alt=""
            aria-hidden="true"
          />
        </button>
      ) : hideNavButton ? null : (
        <span className="control-btn-back" aria-hidden="true">
          <span className="caret caret--left" />
        </span>
      )}
      {biteSelected && bookTitle ? (
        <h1 className="app-header-book-title">
          <span className="app-header-book-title-text">
            {titleParts ? (
              <>
                {titleParts.main} <i>{titleParts.small}</i>
              </>
            ) : (
              bookTitle
            )}
          </span>
          {bitePosition != null && biteTotal > 0 && (
            <small>
              Bite {bitePosition}
              <small>/{biteTotal}</small>
            </small>
          )}
        </h1>
      ) : (
        <>
          <div
            className="app-header-brand"
            onClick={brandTogglesMenu ? onMenuClick : (e) => e.stopPropagation()}
            onKeyDown={
              brandTogglesMenu
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onMenuClick();
                    }
                  }
                : undefined
            }
            tabIndex={brandTogglesMenu ? 0 : undefined}
            role={brandTogglesMenu ? 'button' : undefined}
            aria-label={brandTogglesMenu ? (menuOpen ? 'Close book list' : 'Open book list') : undefined}
            aria-expanded={brandTogglesMenu ? !!menuOpen : undefined}
          >
            <div
              className="app-header-logo"
              onClick={clickable ? onBookBack : undefined}
              onKeyDown={
                logoIsButton
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onBookBack();
                      }
                    }
                  : undefined
              }
              tabIndex={logoIsButton ? 0 : undefined}
              role={logoIsButton ? 'button' : undefined}
              aria-label={logoIsButton ? (backLabel || 'Back') : undefined}
            >
              <img
                className="app-header-logo-l"
                src={`${process.env.PUBLIC_URL}/logo-L.svg`}
                alt="LoreBites"
                width="13"
                height="32"
              />  
              <img
                className="app-header-logo-ore logo-smalls"
                src={`${process.env.PUBLIC_URL}/logo-ore.svg`}
                alt="Ore"
                width="64"
                height="32"
              />
              <img
                className="app-header-logo-b"
                src={`${process.env.PUBLIC_URL}/logo-B.svg`}
                alt="LoreBites"
                width="26"
                height="32"
              />
              <img
                className="app-header-logo-ites logo-smalls"
                src={`${process.env.PUBLIC_URL}/logo-ites.svg`}
                alt="Ites"
                width="64"
                height="32"
              />
            </div>
            {!aboutLinkVisible ? null : (
              <Link
                to="/about"
                className={`app-header-about${location.pathname === '/about' ? ' app-header-about-current' : ''}`}
                onClick={(e) => e.stopPropagation()}
              >
                About
              </Link>
            )}
          </div>
          {comingSoon && (
            <h2 className="app-header-coming-soon">
              Coming Soon!
            </h2>
          )}
        </>
      )}
      {children}
    </div>
  );
}

function parseRoute(pathname) {
  if (pathname === '/') {
    return { page: null, bookName: null, biteNumber: null };
  }
  const segments = pathname.slice(1).split('/').filter(Boolean);
  const first = segments[0] || null;
  if (first === 'about') {
    return { page: 'about', bookName: null, biteNumber: null };
  }
  const bookName = first;
  const biteSegment = segments[1];
  const parsedBite = biteSegment != null ? parseInt(biteSegment, 10) : null;
  const biteNumber =
    parsedBite != null && Number.isFinite(parsedBite) && parsedBite > 0 ? parsedBite : null;
  return { page: null, bookName, biteNumber };
}

function bookPath(bookName, biteNumber) {
  const base = `/${bookName}`;
  return biteNumber ? `${base}/${biteNumber}` : base;
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { page, bookName, biteNumber } = parseRoute(location.pathname);
  const isAbout = page === 'about';
  const [books, setBooks] = useState(bookList);
  const book = books.find((b) => b.name === bookName);
  const unknownBook = !!bookName && !book;
  const isCompact = !!bookName && !unknownBook;
  const biteSelected = biteNumber != null && !unknownBook;
  const bookTitle = book?.title;

  const bookOgImage = book?.name
    ? `${BLOBS_BASE_URL}/bites/${book.name}/book-cover.jpg`
    : '/og.jpg';
  const pageMeta = isAbout
    ? {
        title: 'About',
        description:
          'How LoreBites compresses books into narrated rhymed Bites with holographic foil-card artwork.',
        path: '/about',
      }
    : unknownBook
      ? {
          title: 'Not found',
          description: 'This page does not exist on LoreBites.',
          path: location.pathname,
          noIndex: true,
        }
      : book
        ? {
            title: biteSelected
              ? `${book.title} · Bite ${biteNumber}`
              : book.title,
            description: bookPageDescription(book),
            path: biteSelected ? `/${book.name}/${biteNumber}` : `/${book.name}`,
            image: bookOgImage,
          }
        : {
            title: 'LoreBites',
            description:
              'Classic and favorite books compressed into narrated rhymed Bites — holographic foil cards with word-level captions.',
            path: '/',
          };
  usePageMeta(pageMeta);
  const totalBites = useMemo(
    () => (book?.bites ?? []).reduce((sum, count) => sum + count, 0),
    [book],
  );
  const bookIndex = books.findIndex((b) => b.name === bookName);
  const [compactDrawerSnap, setCompactDrawerSnap] = useState(false);
  const [drawerHoverLocked, setDrawerHoverLocked] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 768px)').matches);
  const [mobileListening, setMobileListening] = useState(false);
  const showMobileBookDrawer = isCompact && isMobile;
  const bookRef = useRef(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleChange = () => setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [bookName, biteNumber, location.pathname]);

  useEffect(() => {
    setMobileListening(false);
  }, [bookName]);

  useEffect(() => {
    if (mobileListening) {
      setMobileDrawerOpen(false);
    }
  }, [mobileListening]);

  useEffect(() => {
    document.documentElement.classList.toggle('mobile-listening', mobileListening);
    document.body.classList.toggle('mobile-listening', mobileListening);
    return () => {
      document.documentElement.classList.remove('mobile-listening');
      document.body.classList.remove('mobile-listening');
    };
  }, [mobileListening]);

  useEffect(() => {
    document.documentElement.classList.toggle('books-mobile-drawer-open', mobileDrawerOpen);
    document.body.classList.toggle('books-mobile-drawer-open', mobileDrawerOpen);
    return () => {
      document.documentElement.classList.remove('books-mobile-drawer-open');
      document.body.classList.remove('books-mobile-drawer-open');
    };
  }, [mobileDrawerOpen]);

  useEffect(() => {
    const lockHtmlScroll = isMobile && isCompact;
    document.documentElement.classList.toggle('mobile-lock-html-scroll', lockHtmlScroll);
    document.body.classList.toggle('mobile-lock-html-scroll', lockHtmlScroll);
    if (lockHtmlScroll) {
      window.scrollTo(0, 0);
    }
    return () => {
      document.documentElement.classList.remove('mobile-lock-html-scroll');
      document.body.classList.remove('mobile-lock-html-scroll');
    };
  }, [isMobile, isCompact]);

  const handleBooksLoaded = useCallback((booksData) => {
    setBooks(booksData);
  }, []);

  const onBiteRouteChange = useCallback(
    (nextBiteNumber) => {
      if (!bookName) return;
      const target = bookPath(bookName, nextBiteNumber || null);
      if (location.pathname === target) return;
      navigate(target);
    },
    [bookName, navigate, location.pathname],
  );

  useEffect(() => {
    document.documentElement.classList.toggle('bite-selected', biteSelected);
    document.body.classList.toggle('bite-selected', biteSelected);
    return () => {
      document.documentElement.classList.remove('bite-selected');
      document.body.classList.remove('bite-selected');
    };
  }, [biteSelected]);

  useEffect(() => {
    if (!isCompact || isMobile) {
      setCompactDrawerSnap(false);
      return;
    }
    setCompactDrawerSnap(true);
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setCompactDrawerSnap(false));
    });
    return () => cancelAnimationFrame(frame);
  }, [isCompact, isMobile, bookName]);

  useEffect(() => {
    if (!isCompact || isMobile) {
      setDrawerHoverLocked(false);
    }
  }, [isCompact, isMobile]);

  useEffect(() => {
    document.documentElement.classList.toggle('books-drawer-hover-locked', drawerHoverLocked);
    document.body.classList.toggle('books-drawer-hover-locked', drawerHoverLocked);
    return () => {
      document.documentElement.classList.remove('books-drawer-hover-locked');
      document.body.classList.remove('books-drawer-hover-locked');
    };
  }, [drawerHoverLocked]);

  useEffect(() => {
    if (!drawerHoverLocked) return;
    const unlockIfLeft = () => {
      const overLayer = document.querySelector('.books-layer.compact:hover');
      const overHeader = document.querySelector('.books-list-compact-book-header.compact:hover');
      if (!overLayer && !overHeader) setDrawerHoverLocked(false);
    };
    document.addEventListener('pointermove', unlockIfLeft);
    document.addEventListener('pointerdown', unlockIfLeft);
    return () => {
      document.removeEventListener('pointermove', unlockIfLeft);
      document.removeEventListener('pointerdown', unlockIfLeft);
    };
  }, [drawerHoverLocked]);

  const handleBookSelect = useCallback((selectedBookName) => {
    setMobileDrawerOpen(false);
    const wasTryOtherOpen = document.body.classList.contains('books-try-other-open');
    if (wasTryOtherOpen && isCompact && !isMobile) {
      setDrawerHoverLocked(true);
      document.documentElement.classList.add('books-drawer-hover-locked');
      document.body.classList.add('books-drawer-hover-locked');
      document.documentElement.classList.remove('books-try-other-open');
      document.body.classList.remove('books-try-other-open');
    }
    navigate(`/${selectedBookName}`);
  }, [isCompact, isMobile, navigate]);

  const handleHome = () => {
    setMobileDrawerOpen(false);
    navigate('/');
  };

  const handleBookBack = () => {
    navigate('/');
  };

  const handleHeaderBack = () => {
    if (biteSelected) {
      bookRef.current?.goToUnselectedBite?.();
      return;
    }
    if (showMobileBookDrawer) {
      setMobileDrawerOpen((open) => !open);
      return;
    }
    handleBookBack();
  };

  const wallpaperStyle = isMobile
    ? undefined
    : {
        '--books-wallpaper': `url(${process.env.PUBLIC_URL}/wallpaper.webp)`,
      };

  if (isAbout) {
    const aboutFrom = location.state?.from;
    const returnToBook =
      typeof aboutFrom === 'string' &&
      aboutFrom.startsWith('/') &&
      aboutFrom !== '/' &&
      !aboutFrom.startsWith('/about');
    const handleAboutBack = () => {
      if (returnToBook) {
        navigate(aboutFrom);
        return;
      }
      handleHome();
    };

    return (
      <div className="app-container no-book" style={wallpaperStyle}>
        {createPortal(
          <div className="books-list-compact-book-header full">
            <AppHeader
              showBack
              dimLogo
              onBookBack={handleAboutBack}
              backLabel={returnToBook ? 'Back to book' : 'Back to home'}
            />
          </div>,
          document.body,
        )}
        <Suspense fallback={null}>
          <About />
        </Suspense>
      </div>
    );
  }

  if (unknownBook) {
    return (
      <div className="app-container no-book" style={wallpaperStyle}>
        {createPortal(
          <div className="books-list-compact-book-header full">
            <AppHeader
              showBack
              dimLogo
              onBookBack={handleHome}
              backLabel="Back to home"
            />
          </div>,
          document.body,
        )}
        <NotFound />
      </div>
    );
  }

  return (
    <div
      className={`app-container ${isCompact ? 'has-book' : 'no-book'}${biteSelected ? ' bite-selected' : ''}`}
      style={wallpaperStyle}
    >
      {createPortal(
        <div className={`books-list-compact-book-header ${isCompact ? 'compact' : 'full'}`}>
          <AppHeader
            isBookHeader={isCompact}
            onBookBack={handleHeaderBack}
            biteSelected={biteSelected}
            bookTitle={bookTitle}
            bitePosition={biteSelected ? biteNumber : null}
            biteTotal={totalBites}
            backLabel={biteSelected ? 'Close bite' : showMobileBookDrawer ? 'Open book list' : 'Back to home'}
            showMenuButton={showMobileBookDrawer && !biteSelected && !mobileListening}
            menuOpen={mobileDrawerOpen}
            onMenuClick={handleHeaderBack}
            hideNavButton={isMobile && mobileListening}
            comingSoon={isCompact && !!book && !book.prodReady}
          />
        </div>,
        document.body,
      )}
      {showMobileBookDrawer &&
        createPortal(
          <div
            className={`books-mobile-drawer${mobileDrawerOpen ? ' books-mobile-drawer-open' : ''}`}
            aria-hidden={!mobileDrawerOpen}
          >
            <button
              type="button"
              className="books-mobile-drawer-backdrop"
              aria-label="Close book list"
              tabIndex={mobileDrawerOpen ? 0 : -1}
              onClick={() => setMobileDrawerOpen(false)}
            />
            <div className="books-mobile-drawer-panel">
              <button type="button" className="books-mobile-drawer-home control-btn" onClick={handleHome}>
                <span className="caret caret--left" />
                Home
              </button>
              <BooksList
                compact={false}
                hideIntro
                selectedBookName={bookName}
                onBookSelect={handleBookSelect}
                onBooksLoaded={handleBooksLoaded}
              />
            </div>
          </div>,
          document.body,
        )}
      {!showMobileBookDrawer && (
      <div
        className={`books-layer ${isCompact ? 'compact' : 'full'}${compactDrawerSnap ? ' books-layer-compact-drawer-snap' : ''}`}
      >
        <BooksList 
          compact={isCompact} 
          selectedBookName={bookName}
          onBookSelect={handleBookSelect}
          onBooksLoaded={handleBooksLoaded}
        />
      </div>
      )}
      <div className={`book-layer ${bookName ? 'visible' : 'hidden'}`}>
        {bookName ? (
          <Suspense fallback={null}>
            <Book
              ref={bookRef}
              key={bookName}
              bookName={bookName}
              book={book}
              bookIndex={bookIndex}
              biteNumber={biteNumber}
              onBiteRouteChange={onBiteRouteChange}
              onMobileListeningChange={setMobileListening}
            />
          </Suspense>
        ) : null}
      </div>
    </div>
  );
}

export default App;
